/**
 * Lokalno skladište vozačevog telefona.
 *
 * Tura se učita jednom, pre polaska, i od tada živi u localStorage. Sve što
 * vozač uradi ide u red za slanje, isto lokalno. Mreža se ne čeka ni za jedan
 * potez — ako je ima, red se prazni u pozadini; ako je nema, red raste i vozač
 * vidi koliko toga još nije otišlo.
 *
 * Zapisi se brišu tek kad server potvrdi. Server prima ponovljeno slanje bez
 * posledica, pa je bolje poslati dvaput nego izgubiti jednom.
 */

const TOUR_KEY = 'elbraco.vozac.tura';

/**
 * Cija je tura u kesu.
 *
 * Bez ovoga je ekran pokazivao poslednju ucitanu turu bilo kom vozacu koji
 * otvori link, jer se tura cita iz localStorage pre nego sto se bilo sta pita
 * server. Na sluzbenom telefonu koji menja ruke to je tudja tura na ekranu.
 */
const TOUR_DRIVER_KEY = 'elbraco.vozac.id';
const OUTBOX_KEY = 'elbraco.vozac.red';
const OPEN_STOP_KEY = 'elbraco.vozac.otvorena';
const NAV_SEEN_KEY = 'elbraco.vozac.navigacija';

export interface DriverStopItem {
  id: number;
  articleCode: string;
  articleName: string;
  quantity: number;
  isDelivered: boolean;
  deliveredAt: string | null;
}

export interface DriverStop {
  id: number;
  sequence: number;
  plannedSequence: number;
  kind: 'NetworkLocation' | 'CustomerAddress';
  /**
   * Roba se ovde UZIMA, ne ostavlja - prodavnica koja je aparat prodala.
   * Sve ostalo na ekranu je isporuka, pa se ovo mora videti odmah.
   */
  isPickup: boolean;
  name: string;
  city: string | null;
  latitude: number | null;
  longitude: number | null;
  floor: number | null;
  hasLift: boolean | null;
  accessNotes: string | null;
  requiresTwoPeople: boolean;
  /**
   * Na otpremnici pise "bb" - kuca nema broj, pa je tacka na ulici a ne na
   * kapiji. Vozac to mora videti pre nego sto krene da trazi brojeve.
   */
  withoutHouseNumber: boolean;
  /**
   * Kupcevi telefoni. Za adresu upisanu kao "bb" ovo je jedino resenje.
   * Ume ih biti vise - ERP u istom polju cuva i broj drugog ukucanina.
   */
  phones: string[];
  actualArrival: string | null;
  actualDeparture: string | null;
  exceptionReason: string | null;
  notes: string | null;
  hasSignature: boolean;
  items: DriverStopItem[];
}

export interface DriverTour {
  id: number;
  name: string;
  date: string;
  status: string;
  vehicle: string | null;
  driver: string | null;
  actualStart: string | null;
  actualEnd: string | null;
  start: { name: string; latitude: number | null; longitude: number | null };
  end: { name: string; latitude: number | null; longitude: number | null };
  stops: DriverStop[];
}

export interface DriverEvent {
  clientEventId: string;
  type: string;
  occurredAt: string;
  stopId?: number;
  stopItemId?: number;
  text?: string;
  sequence?: number;
  signature?: string;
}

export const EventType = {
  TourStarted: 'tura.krenula',
  ArrivedAtStop: 'stanica.stigao',
  DepartedFromStop: 'stanica.otisao',
  ItemDelivered: 'stavka.isporucena',
  StopException: 'stanica.izuzetak',
  StopReordered: 'stanica.redosled',
  SignatureCaptured: 'stanica.potpis',
  NoteAdded: 'stanica.beleska',
  TourFinished: 'tura.zavrsena'
} as const;

/**
 * Lokalno vreme uređaja, bez vremenske zone.
 *
 * Namerno se ne šalje UTC ni offset: beleži se sat koji je vozač gledao kad je
 * predao robu. Vreme sinhronizacije server upisuje sam, i razlika između ta dva
 * je podatak o tome koliko se radilo bez signala.
 */
export function deviceNow(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
    + `T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
  );
}

function newId(): string {
  // crypto.randomUUID ne postoji na starijim Android pregledačima.
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    // Puno skladište je stvaran rizik zbog potpisa. Ne ruši se ekran, ali se
    // greška ne guta — vozač mora videti da nešto nije sačuvano.
    console.error('Lokalno skladište ne prima više zapisa.', e);
    throw e;
  }
}

export const storage = {
  /**
   * Koja je stanica bila otvorena.
   *
   * Vozač sa ovog ekrana odlazi u Mape, i telefon aplikaciju tada ume da ugasi
   * u pozadini. Kad se vrati, nastavlja tamo gde je stao — a ne traži svoju
   * stanicu među četrnaest.
   */
  openStop: (): number | null => {
    const value = read<number | null>(OPEN_STOP_KEY, null);
    return typeof value === 'number' ? value : null;
  },

  rememberOpenStop: (stopId: number | null) => write(OPEN_STOP_KEY, stopId),

  /** Da li je vozaču već objašnjeno kako se vraća sa Mapa. */
  navigationExplained: (): boolean => read<boolean>(NAV_SEEN_KEY, false) === true,

  markNavigationExplained: () => write(NAV_SEEN_KEY, true),

  /**
   * Tura iz kesa, bez obzira cija je. Koristi je sinhronizacija: red mora da
   * se isprazni i kad je na ekranu neko drugi.
   */
  loadTour: (): DriverTour | null => read<DriverTour | null>(TOUR_KEY, null),

  /**
   * Cijim kljucem je tura ucitana, ili nista ako kes jos nije stampan.
   *
   * Pamti se da bi se tudja tura iz kesa prepoznala: telefon zna da predje iz
   * ruke u ruku, a vozac ne sme da vidi tudje kupce.
   */
  tourDriverKey: (): string | null => read<string | null>(TOUR_DRIVER_KEY, null),

  saveTour: (tour: DriverTour, accessKey: string) => {
    write(TOUR_KEY, tour);
    write(TOUR_DRIVER_KEY, accessKey);
  },

  clearTour: () => {
    localStorage.removeItem(TOUR_KEY);
    localStorage.removeItem(TOUR_DRIVER_KEY);
  },

  outbox: (): DriverEvent[] => read<DriverEvent[]>(OUTBOX_KEY, []),

  /** Upisuje događaj u red. Vraća ga, da pozivalac odmah primeni i lokalno. */
  enqueue: (event: Omit<DriverEvent, 'clientEventId' | 'occurredAt'>): DriverEvent => {
    const full: DriverEvent = { ...event, clientEventId: newId(), occurredAt: deviceNow() };
    write(OUTBOX_KEY, [...read<DriverEvent[]>(OUTBOX_KEY, []), full]);
    return full;
  },

  /** Briše samo ono što je server potvrdio. Odbijeno namerno ostaje. */
  forget: (ids: string[]) => {
    const keep = new Set(ids);
    write(OUTBOX_KEY, read<DriverEvent[]>(OUTBOX_KEY, []).filter((e) => !keep.has(e.clientEventId)));
  }
};
